/**
 * Auth/session module (CLAUDE.md working rule #8 "an auth module (service + state, the
 * token/localStorage logic)"). The single owner of "who is logged in right now" — `app-shell.ts`
 * (route guards) and every page subscribe to it instead of touching `localStorage` or
 * `api/http.ts`'s storage helpers directly.
 *
 * Auth-first flow (docs/AGENT_STATUS.md "OWNER HANDS-ON TEST punch-list ROUND 2" #2): the 3D
 * scene must never be constructed before a route decides it's needed — this module has ZERO
 * Babylon/DOM-page dependencies, so `app-shell.ts` can resolve "am I logged in?" before mounting
 * anything.
 */
import { updateWork } from "../api/account-api";
import { noteWork } from "./user-role";
import { confirmEmailLoginRequest, loginRequest, logoutRequest, refreshRequest, registerRequest } from "../api/auth-api";
import { clearAllSignedInDrafts } from "../editor/signed-in-draft";
import {
  ApiError,
  clearStoredAuth,
  getStoredRefreshToken,
  getStoredUser,
  setSessionRefresher,
  setUnauthorizedHandler,
  storeAuth,
  type SessionRefreshResult,
  type StoredAuthUser,
} from "../api/http";

export type AuthUser = StoredAuthUser;

type Listener = (user: AuthUser | null) => void;

/**
 * Session state, restored synchronously from localStorage at module load (so a hard reload / a
 * deep link to `/editor?rider=<id>` doesn't flash a false "logged out" state before anything
 * async resolves).
 */
let currentUser: AuthUser | null = getStoredUser();
const listeners = new Set<Listener>();

function notify(): void {
  for (const listener of listeners) listener(currentUser);
}

export function getCurrentUser(): AuthUser | null {
  return currentUser;
}

export function isLoggedIn(): boolean {
  return currentUser !== null;
}

/** Fires immediately with the current state, then on every subsequent change. Returns an unsubscribe fn. */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  listener(currentUser);
  return () => listeners.delete(listener);
}

/**
 * «As soon as the person makes an account, everything the browser kept moves into it» (owner, Viktor, 27.10.2026: «как только
 * человек заводит аккаунт»). The warehouse and its companions are moved by `equipment/account-move.ts`; this module knows
 * nothing about them (it has no page or store dependencies by design) — it only gives a hook the moment: `register`,
 * `loginWithEmailLink` and `login` await it right after the session is set and the subscribers told, so when they resolve
 * the account already holds what the guest had. It waits at most `ENTER_WAIT_MS`: a slow or dead network must not keep a
 * person at the sign-in form, and a move that did not finish is simply tried again at the next load of the warehouse.
 */
type AccountEnteredHook = (user: AuthUser) => Promise<unknown>;
let accountEnteredHook: AccountEnteredHook | null = null;
const ENTER_WAIT_MS = 4000;

export function onAccountEntered(hook: AccountEnteredHook | null): void {
  accountEnteredHook = hook;
}

async function afterEnter(user: AuthUser): Promise<void> {
  const hook = accountEnteredHook;
  if (!hook) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([hook(user), new Promise<void>((resolve) => (timer = setTimeout(resolve, ENTER_WAIT_MS)))]);
  } catch {
    /* the account is made and entered; the move retries on the warehouse's next load */
  } finally {
    clearTimeout(timer);
  }
}

export async function login(email: string, password: string): Promise<AuthUser> {
  const result = await loginRequest(email, password);
  storeAuth(result.accessToken, result.user, result.refreshToken);
  currentUser = result.user;
  notify();
  await afterEnter(result.user);
  return result.user;
}

/**
 * `options.userRole` — роль с плашки на форме регистрации (необязательная, владелец 19.10.2026).
 * Пишется тем же `PUT /users/me/work`, что и из профиля, СРАЗУ после создания аккаунта и ДО
 * оповещения подписчиков: оболочка, выбирая первый экран, уже её знает (`noteWork` кладёт роль и в
 * сохранённого человека, откуда её читает `app-shell.ts`). Не записалась — не беда: аккаунт создан,
 * а роль меняется в профиле; регистрацию это не отменяет.
 *
 * `options.via` — форма открыта из «Make offer»: сервер сделает аккаунт прокатчиком (SCRUM-647,
 * `register-via.ts`). Плашку роли на этом пути не показывают, так что вместе они не встречаются.
 */
export async function register(
  email: string,
  password: string,
  options: { userRole?: string; via?: "offer"; displayName?: string } = {}
): Promise<AuthUser> {
  const result = await registerRequest(email, password, options.via, options.displayName);
  storeAuth(result.accessToken, result.user, result.refreshToken);
  if (options.userRole) {
    try {
      noteWork(await updateWork({ company: "", userRole: options.userRole }));
    } catch {
      /* see the doc comment above */
    }
  }
  currentUser = result.user;
  notify();
  await afterEnter(result.user);
  return result.user;
}

/** Signs in with the one-time link from the letter (SCRUM-591) — the account may be brand new,
 *  created by this very call. */
export async function loginWithEmailLink(
  token: string
): Promise<{ user: AuthUser; offers: Array<{ riderId: string; riderVersion: number; offerId?: string; status: string }> }> {
  const result = await confirmEmailLoginRequest(token);
  storeAuth(result.accessToken, result.user, result.refreshToken);
  currentUser = result.user;
  notify();
  await afterEnter(result.user);
  return { user: result.user, offers: result.offers ?? [] };
}

/** Forgets the session locally, with no server call and without touching any draft. */
function endSession(): void {
  clearStoredAuth();
  currentUser = null;
  notify();
}

/**
 * The person's own act of signing out. Two things happen here that do NOT happen on a forced
 * sign-out, and both were added on 2026-10-11 by work that landed in the same batch:
 *
 *  - every unsaved-work draft of that user is taken (SCRUM-728) — on a shared machine the next
 *    person must not inherit it;
 *  - the server is told to forget the refresh token (SCRUM-729) — the access token is stateless and
 *    just dies, but the refresh token outlives the tab, so it has to be revoked. Fire-and-forget: a
 *    failed call must not keep someone signed in, and the token is gone from this browser anyway.
 *
 * `forceLogout` (a 401) deliberately does NEITHER: keeping the draft across an expired session is
 * the whole point of the draft, and the refresh token is the very thing the server just refused.
 */
export function logout(): void {
  const userId = currentUser?.id;
  if (userId) clearAllSignedInDrafts(userId);
  const refreshToken = getStoredRefreshToken();
  if (refreshToken) void logoutRequest(refreshToken).catch(() => undefined);
  endSession();
}

/** Set by `forceLogout()`, read once by `login-page.ts` on mount (`consumeSessionExpiredFlag`) to
 *  show "your session has expired" instead of a blank login form — see that function's doc comment. */
let sessionExpired = false;

/** Called by `api/http.ts#authFetch` on a 401 from an authenticated call (expired/revoked token) —
 *  forces the app back to a logged-out state so app-shell's route guard redirects to /login, and
 *  arms the banner `login-page.ts` shows there. Idempotent by design: several requests can 401 in
 *  the same burst (a page firing riders + account in parallel, say), and each calls this — but
 *  `logout()`/`notify()` only really fire once (`currentUser` is already null on the 2nd+ call),
 *  so app-shell's route guard only ever redirects once, never a storm of re-navigations. */
export function forceLogout(): void {
  sessionExpired = true;
  if (currentUser === null) return; // already logged out — nothing left to notify
  // NOT `logout()`: the unsaved-work draft must survive a session that merely expired (SCRUM-728),
  // and the refresh token is the very thing the server just refused (SCRUM-729).
  endSession();
}

// --- renewing the session (SCRUM-729) ---------------------------------------------------------
// The access token lives 15 minutes; the refresh token 30 days, sliding. THIS module is the only
// place that exchanges one for the other. `api/http.ts#authFetch` asks, on a 401.

/** The exchange in flight. Every caller that 401s while it runs waits on THIS promise: refresh tokens
 *  are single-use, so a second exchange with the same token would be read as theft by the server. */
let inflightRefresh: Promise<SessionRefreshResult> | null = null;

export function refreshSession(): Promise<SessionRefreshResult> {
  inflightRefresh ??= runExclusive(exchangeRefreshToken).finally(() => {
    inflightRefresh = null;
  });
  return inflightRefresh;
}

/** Serialises the exchange across TABS of this browser too, where the Web Locks API exists: two tabs
 *  exchanging the same token would be the same theft signal. Without it (old browsers, tests) the
 *  in-tab promise above is all there is. */
function runExclusive<T>(task: () => Promise<T>): Promise<T> {
  const locks = (typeof navigator === "undefined" ? undefined : (navigator as Navigator & { locks?: LockManager }).locks);
  return locks ? (locks.request("scb-refresh-session", task) as Promise<T>) : task();
}

async function exchangeRefreshToken(): Promise<SessionRefreshResult> {
  const sent = getStoredRefreshToken();
  if (!sent) return "rejected"; // a session from before refresh tokens: nothing to exchange
  try {
    const result = await refreshRequest(sent);
    if (getStoredRefreshToken() !== sent || !result.refreshToken) {
      // The person signed out (or another tab rotated it) while we waited: do not resurrect or overwrite.
      return getStoredRefreshToken() ? "renewed" : "rejected";
    }
    storeAuth(result.accessToken, result.user, result.refreshToken);
    const changed = JSON.stringify(currentUser) !== JSON.stringify(result.user);
    currentUser = result.user;
    if (changed) notify();
    return "renewed";
  } catch (err) {
    if (err instanceof ApiError && (err.status === 400 || err.status === 401 || err.status === 403)) {
      forceLogout();
      return "rejected";
    }
    return "unavailable"; // network or server trouble: the session may be fine, do not end it
  }
}

/** Consumes (reads-then-clears) the "was this login screen forced by an expired session?" flag —
 *  called once by `login-page.ts` on mount. Clearing on read means a page refresh on /login after
 *  the message was already shown doesn't keep re-showing it. */
export function consumeSessionExpiredFlag(): boolean {
  const was = sessionExpired;
  sessionExpired = false;
  return was;
}

// Registered here (not in api/http.ts, which would create an import cycle: http.ts is a
// transitive dependency of THIS file via auth-api.ts) so any authenticated call's 401 routes back
// to `forceLogout` above.
setUnauthorizedHandler(forceLogout);
setSessionRefresher(refreshSession);
